<template>
    <Breadcrumb :items="breadcrumbs" />
    <div class="d-flex justify-content-end mb-3">
        <router-link :to="absenceReportRoute" class="btn PrimaryButton">
            <i class="pi pi-chart-bar me-1" aria-hidden="true"></i> تقرير الغياب
        </router-link>
    </div>
    <Loading v-if="loading" />

    <!-- Barcode Scanner panel -->
    <div class="card mb-3 border-primary" v-if="!loading">
        <div class="card-body py-3">
            <h6 class="card-title mb-3">
                <i class="pi pi-barcode me-1"></i> تسجيل الحضور بالباركود
            </h6>
            <div class="form-group mb-3" style="max-width: 220px;">
                <label for="attendance-scan-date">تاريخ تسجيل الحضور</label>
                <input id="attendance-scan-date" v-model="scanDate" type="date" class="form-control">
                <small class="d-block text-muted mt-1">الافتراضي هو تاريخ اليوم. غيّره لتسجيل الحضور بتاريخ آخر.</small>
            </div>
            <BarcodeScanner mode="attendance" @scanned="handleAttendanceQR" />
            <!-- Reserve feedback space so scan progress cannot shift the viewport. -->
            <div :class="{ invisible: !scanLookupLoading }" class="mt-2 text-primary small">
                <span class="spinner-border spinner-border-sm me-1"></span>
                جاري البحث عن الطالب...
            </div>
        </div>
    </div>

    <div class="row mx-0" v-if="!loading">
        <div class="col-lg-4 col-md-12 col-12">
            <div class="form-group">
                <label for="search">ابحث</label>
                <input type="search" id="search" class="form-control" placeholder="ابحث" v-model="word"
                    @keyup="debounceFetchAttendance">
            </div>
        </div>
        <div class="col-lg-2 col-md-6 col-12">
            <div class="form-group d-flex flex-column">
                <label>الشهور</label>
                <Dropdown v-model="selectedMonth" :options="months" showClear optionLabel="name" placeholder="الشهور"
                    @change="fetchAttendance" />
            </div>
        </div>
        <div class="col-lg-2 col-md-6 col-12">
            <div class="form-group d-flex flex-column">
                <label>الجنس</label>
                <Dropdown v-model="gender" :options="gender_list" showClear optionLabel="title" placeholder="الجنس"
                    @change="fetchAttendance" />
            </div>
        </div>
        <div class="col-lg-2 col-md-3 col-12">
            <div class="d-flex">
                <div class="form-group d-flex flex-column mx-1">
                    <label class="non_opacity">Excel</label>
                    <button class="btn btn-success px-3" @click="exportToExcel">
                        <i class="pi pi-file-excel"></i>
                    </button>
                </div>
                <div class="form-group d-flex flex-column mx-1">
                    <label class="non_opacity">طباعة</label>
                    <button class="btn btn-danger px-3" v-print="'#reservationTable'">
                        <i class="pi pi-file-pdf"></i>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Bulk absence notify row -->
    <div class="row mx-0 mb-3 mt-2" v-if="!loading">
        <div class="col-12">
            <div class="d-flex align-items-center gap-2 flex-wrap">
                <label class="mb-0 fw-semibold">إشعارات الغياب ليوم:</label>
                <input type="date" class="form-control" style="max-width:180px" v-model="bulkNotifyDate" />
                <button class="btn btn-warning" @click="sendBulkAbsentNotify" :disabled="bulkNotifying">
                    <i class="pi pi-bell me-1"></i>
                    {{ bulkNotifying ? 'جاري الإرسال...' : 'إرسال إشعارات الغياب' }}
                </button>
            </div>
        </div>
    </div>

    <div class="table-responsive attendance-table" v-if="!loading" id="reservationTable">
        <table class="table table-bordered">
            <thead>
                <tr>
                    <th>الاسم</th>
                    <th v-for="day in days" :key="day">
                        <p class="mb-0">{{ getDayName(day) }}</p>
                        <p class="mb-0">{{ formatDayMonth(day) }}</p>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="student in students" :key="student.user_id"
                    :data-student-row="student.user_id"
                    :class="{ 'row-highlight': highlightedUserId === student.user_id }">
                    <td>
                        {{ student.name }}
                        <span v-if="student.attendanceDates.length" class="text-success fw-bold">
                            ({{ student.attendanceDates.length }})
                        </span>
                        <small v-if="student.reservation_number != null" class="d-block text-muted">
                            رقم الحجز: <bdi>{{ student.reservation_number }}</bdi>
                        </small>
                    </td>
                    <td v-for="day in days" :key="day" class="text-center att-cell"
                        :class="student.attendanceDates.includes(day) ? 'att-present' : ''"
                        @click="toggleAttendance(student.user_id, day)">
                        <i v-if="student.attendanceDates.includes(day)" class="pi pi-check att-icon-present"></i>
                        <i v-else class="pi pi-times att-icon-absent"></i>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Absence notification dialog -->
    <Dialog v-model:visible="visible" modal header="رسالة الغياب" :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <Textarea v-model="attendance_message" style="display: block; width: 100%;" cols="30" rows="5" />
        <div class="text-end mt-1" :class="attendance_message.length > 70 ? 'text-danger' : 'text-success'">
            {{ attendance_message.length }} حرف
            <span class="ms-2">عدد الرسائل: {{ smsCount }}</span>
        </div>
        <button type="button" class="PrimaryButton mt-3" @click="SendAttendanceMessage">إرسال</button>
    </Dialog>
</template>

<script>
import axios from "axios";
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import Swal from 'sweetalert2';
import Textarea from 'primevue/textarea';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import BarcodeScanner from '@/components/global/BarcodeScanner.vue';
import * as XLSX from 'xlsx/xlsx.mjs';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
    components: { Dropdown, Dialog, Textarea, Breadcrumb, Loading, BarcodeScanner },

    data() {
        return {
            students: [],
            days: [],
            selectedMonth: {
                id: new Date().getMonth() + 1,
                name: new Date().toLocaleString('default', { month: 'long' }),
            },
            months: [
                { id: 1,  name: 'يناير'  }, { id: 2,  name: 'فبراير' },
                { id: 3,  name: 'مارس'   }, { id: 4,  name: 'أبريل'  },
                { id: 5,  name: 'مايو'   }, { id: 6,  name: 'يونيو'  },
                { id: 7,  name: 'يوليو'  }, { id: 8,  name: 'أغسطس' },
                { id: 9,  name: 'سبتمبر' }, { id: 10, name: 'أكتوبر' },
                { id: 11, name: 'نوفمبر' }, { id: 12, name: 'ديسمبر' },
            ],
            gender_list: [
                { id: 1, title: 'ولد' },
                { id: 2, title: 'بنت' },
            ],
            word: '',
            gender: null,
            searchTimeout: null,

            // QR scan lookup
            scanLookupLoading: false,
            highlightedUserId: null,
            // Attendance scanning date — defaults to today on every page
            // load/reload; only changes when the admin explicitly picks
            // another date. Sent as-is to POST /attendance/qr on every scan.
            scanDate: new Date().toISOString().slice(0, 10),

            // Individual notification dialog
            visible: false,
            attendance_message: '',
            parent_phone: null,
            attendance_id: null,

            // Bulk notification
            bulkNotifyDate: new Date().toISOString().slice(0, 10),
            bulkNotifying: false,

            // Templates cache (code → template object)
            templates: {},

            loading: false,
        };
    },

    computed: {
        absenceReportRoute() {
            return {
                name: 'gradeAttendanceAbsenceReport',
                params: { gradeSlug: this.gradeSlug },
                query: {
                    month: new Date().getFullYear() + '-' + String(this.selectedMonth?.id ?? new Date().getMonth() + 1).padStart(2, '0'),
                    word: this.word || undefined,
                    gender: this.gender?.id || undefined,
                },
            };
        },
        smsCount() {
            return Math.ceil(this.attendance_message.length / 70) || 1;
        },
        gradeStore() { return useGradeStore(); },
        gradeSlug() { return this.$route.params.gradeSlug; },
        grade() {
            const slug = this.gradeSlug;
            if (!slug) return null;
            return this.gradeStore.activeGrades.find(g => g.slug === slug)
                || this.gradeStore.allGrades.find(g => g.slug === slug)
                || null;
        },
        gradeId() { return this.grade?.id ?? null; },
        breadcrumbs() {
            const gradeName = this.grade?.name ?? this.gradeSlug ?? '';
            const gradePath = this.grade ? `/grades/${this.grade.slug}/students` : '/';
            return [
                { name: 'الرئيسية', path: '/' },
                { name: gradeName, path: gradePath },
                { name: 'الحضور', path: this.$route.fullPath },
            ];
        },
    },

    methods: {
        debounceFetchAttendance() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.fetchAttendance(), 1500);
        },

        getDayName(dateStr) {
            const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
            return days[new Date(dateStr).getDay()];
        },

        formatDayMonth(dateStr) {
            const [, mm, dd] = dateStr.split('-');
            return `${dd}-${mm}`;
        },

        getDaysOfMonth(year, month) {
            const days = [];
            const date = new Date(year, month, 1);
            while (date.getMonth() === month) {
                const yyyy = date.getFullYear();
                const mm   = String(date.getMonth() + 1).padStart(2, '0');
                const dd   = String(date.getDate()).padStart(2, '0');
                days.push(`${yyyy}-${mm}-${dd}`);
                date.setDate(date.getDate() + 1);
            }
            return days;
        },

        async fetchAttendance() {
            const formData = new FormData();
            if (this.gradeId) formData.append('academic_year_id', this.gradeId);
            formData.append('status', 1);
            formData.append('order_by_name', 1);
            if (this.word)   formData.append('word', this.word);
            if (this.gender) formData.append('gender', this.gender.id);

            axios.defaults.headers.common['per_page'] = 100000;
            const resStudents = await axios.post('/all_reservations', formData);
            const allStudents = resStudents.data.data.data || resStudents.data.students || [];

            if (!this.selectedMonth?.id) {
                this.selectedMonth = { id: new Date().getMonth() + 1, name: '' };
            }

            const resAttendance = await axios.get(`/attendance-by-month/${this.selectedMonth.id}`);
            this.loading = false;
            const attendanceList = resAttendance.data.data || [];

            const today = new Date();
            this.days = this.getDaysOfMonth(today.getFullYear(), this.selectedMonth.id - 1);

            this.students = allStudents.map(student => {
                const userId = student.user_id ?? student.id;
                const found = attendanceList.find(a => a.user_id === userId);
                return {
                    user_id:         userId,
                    name:            student.name,
                    // ReservationResource.id is the existing public booking number.
                    reservation_number: student.id,
                    phone:           student.phone,
                    gender:          student.gender,
                    attendanceDates: found ? found.dates : [],
                };
            });
        },

        async loadTemplates() {
            try {
                const res = await axios.get('/whatsapp-templates');
                const list = res.data.data ?? [];
                this.templates = Object.fromEntries(list.map(t => [t.code, t]));
            } catch { /* non-fatal */ }
        },

        buildAbsenceMessage(student, date) {
            const tpl = this.templates['absence_notification'];
            if (tpl?.is_active) {
                return tpl.message
                    .replace(/\{\{student_name\}\}/g, student.name)
                    .replace(/\{\{date\}\}/g, date)
                    .replace(/\{\{teacher_name\}\}/g, 'أ.طارق المحلاوي')
                    .replace(/\{\{academic_year\}\}/g, this.grade?.name ?? '');
            }
            const pronoun = student.gender == 1 ? 'أبنكم' : 'أبنتكم';
            return `أ.طارق المحلاوي مدرس اللغة العربية\nيحيط علم سيادتكم بأن ${pronoun} (${student.name}) لم يحضر يوم ${this.getDayName(date)} الموافق ${this.formatDayMonth(date)}`;
        },

        // Looks up students matching a scanned value (student id, reservation id, or
        // reservation code) within the current grade, via the same search endpoint
        // fetchAttendance() uses. Mirrors payments/index.vue's searchStudentsForScan().
        async searchStudentsForScan(term) {
            const formData = new FormData();
            if (this.gradeId) formData.append('academic_year_id', this.gradeId);
            formData.append('status', 1);
            formData.append('word', term);
            axios.defaults.headers.common['per_page'] = 10000;

            const res = await axios.post('/all_reservations', formData);
            return res.data.data?.data ?? [];
        },

        async chooseFromMultipleMatches(matches) {
            const inputOptions = Object.fromEntries(
                matches.map((s) => [s.user_id, `${s.name}${s.parent_phone ? ' - ' + s.parent_phone : ''}`])
            );
            const { value: chosenId } = await Swal.fire({
                icon: 'question',
                title: 'تم العثور على أكثر من طالب',
                input: 'radio',
                inputOptions,
                confirmButtonText: 'اختيار',
                cancelButtonText: 'إلغاء',
                showCancelButton: true,
            });
            if (!chosenId) return null;
            return matches.find((s) => String(s.user_id) === String(chosenId)) ?? null;
        },

        async handleAttendanceQR({ raw, studentId }) {
            console.log('[Attendance] Callback fired', { raw, studentId });

            const term = studentId ?? raw;
            if (!term) return;

            console.log('[Attendance] Lookup started for:', term);
            this.scanLookupLoading = true;
            try {
                const matches = await this.searchStudentsForScan(term);

                if (!matches.length) {
                    console.log('[Attendance] Lookup failed: no student found for', term);
                    Swal.fire({ icon: 'info', title: 'لم يتم العثور على الطالب', timer: 2500, showConfirmButton: false });
                    return;
                }

                let student;
                if (matches.length === 1) {
                    student = matches[0];
                } else {
                    console.log('[Attendance] Lookup ambiguous, multiple matches:', matches.length);
                    student = await this.chooseFromMultipleMatches(matches);
                    if (!student) return; // user cancelled
                }

                console.log('[Attendance] Lookup success:', student);
                await this.markAttendanceFromScan(student);
            } catch (err) {
                console.log('[Attendance] Lookup failed:', err);
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'تعذّر البحث عن الطالب', timer: 2500, showConfirmButton: false });
            } finally {
                this.scanLookupLoading = false;
            }
        },

        // Marks the resolved student present for today (same effect as clicking today's
        // cell manually), then highlights their row without moving the viewport.
        async markAttendanceFromScan(student) {
            const userId  = student.user_id ?? student.id;
            const timeStr = new Date().toTimeString().slice(0, 8);
            // The scanning page's date picker (defaults to today) — sent
            // explicitly so the backend never has to guess/assume "now" when
            // the admin picked a different date.
            const payload = { user_id: userId };
            if (this.scanDate) payload.date = this.scanDate;

            try {
                const res = await axios.post('/attendance/qr', payload);
                console.log('[Attendance] Page action executed: attendance marked for', userId);

                // Always the date actually recorded by the backend — the
                // request's own scanDate, confirmed back — never the
                // client's own "now", which would be wrong for a past date.
                const recordedDate = res.data.date
                    ? new Date(`${res.data.date}T00:00:00`).toLocaleDateString('ar-EG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                    : '';

                const toast = Swal.mixin({
                    toast: true, position: 'top-end',
                    showConfirmButton: false, timer: 4500, timerProgressBar: true,
                });

                if (res.data.status === 'already_exists') {
                    toast.fire({ icon: 'warning', title: 'تم تسجيل الحضور مسبقاً', html: `<strong>${res.data.student_name}</strong>` });
                } else {
                    toast.fire({
                        icon: 'success',
                        title: 'تم تسجيل حضور الطالب بنجاح',
                        html: `<strong>${res.data.student_name}</strong><br>التاريخ: ${recordedDate}<br>الوقت: ${timeStr}`,
                    });
                }

                await this.fetchAttendance();
                await this.revealStudentRow(userId);
            } catch {
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'تعذّر تسجيل الحضور', timer: 2500, showConfirmButton: false });
            }
        },

        // Ensures the given student's row is actually rendered (clears filters that
        // might otherwise hide it), then flashes a highlight without moving the viewport.
        async revealStudentRow(userId) {
            const alreadyVisible = this.students.some((s) => s.user_id === userId);
            if (!alreadyVisible) {
                this.word = '';
                this.gender = null;
                await this.fetchAttendance();
            }

            await this.$nextTick();

            this.highlightedUserId = userId;
            setTimeout(() => {
                if (this.highlightedUserId === userId) this.highlightedUserId = null;
            }, 2000);
        },

        async toggleAttendance(user_id, date) {
            const res = await axios.post('/attendance', { user_id, date })
                .catch(err => { console.error(err); return null; });

            if (!res) return;

            this.parent_phone  = res.data.parent_phone;
            this.attendance_id = res.data.attendance?.id ?? Date.now();

            await this.fetchAttendance();
        },

        async sendBulkAbsentNotify() {
            if (!this.gradeId) {
                Swal.fire({ icon: 'warning', title: 'تنبيه', text: 'لم يتم تحديد الفصل', timer: 2000, showConfirmButton: false });
                return;
            }

            const result = await Swal.fire({
                title:             'إرسال إشعارات الغياب',
                html:              `سيتم إرسال إشعارات لجميع الطلاب الغائبين يوم <strong>${this.bulkNotifyDate}</strong>`,
                icon:              'question',
                showCancelButton:  true,
                confirmButtonText: 'نعم، إرسال',
                cancelButtonText:  'إلغاء',
            });

            if (!result.isConfirmed) return;

            this.bulkNotifying = true;
            try {
                const res = await axios.post('/attendance/bulk-notify-absent', {
                    academic_year_id: this.gradeId,
                    date:             this.bulkNotifyDate,
                });
                const { total, sent, failed } = res.data;
                Swal.fire({
                    icon:  sent > 0 ? 'success' : 'info',
                    title: 'اكتمل الإرسال',
                    html:  `الغائبون: <strong>${total}</strong><br>تم الإرسال: <strong>${sent}</strong><br>فشل: <strong>${failed}</strong>`,
                    timer: 6000,
                    showConfirmButton: true,
                });
            } catch {
                Swal.fire({ icon: 'error', title: 'خطأ', text: 'حدث خطأ أثناء الإرسال', timer: 3000, showConfirmButton: false });
            } finally {
                this.bulkNotifying = false;
            }
        },

        async SendAttendanceMessage() {
            const phone = '2' + this.parent_phone;

            let hasWhatsApp = false;
            try {
                const r = await axios.post('/whatsapp/check', { phone });
                hasWhatsApp = r.data.has_whatsapp === true;
            } catch { hasWhatsApp = false; }

            if (hasWhatsApp) {
                try {
                    const r = await axios.post('/whatsapp/send', { phone, message: this.attendance_message });
                    if (r.data.success) {
                        Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة عبر واتساب بنجاح', timer: 1500, showConfirmButton: false });
                        this.visible = false;
                        return;
                    }
                } catch { /* fall to SMS */ }
            }

            const formData = new FormData();
            formData.append('SenderName', 'SMS invite');
            formData.append('RequestID', this.attendance_id);
            formData.append('PhoneNumber', phone);
            formData.append('Message', this.attendance_message);

            await axios.post(
                'https://hubapi.advansystelecom.com/api/bulkSMS/ForwardSMS',
                formData,
                { headers: { 'Authorization': '667d0a1e-f2b3-4bd5-8569-360b6e7b80b0', 'Content-Type': 'application/json' } }
            ).then(res => {
                if (res.data === '1') {
                    Swal.fire({ icon: 'success', title: 'تم الإرسال', text: 'تم إرسال الرسالة بنجاح', timer: 1500, showConfirmButton: false });
                    this.visible = false;
                }
            }).catch(err => {
                const code = err.response?.data?.Message;
                const msgs = { '-1': 'رمز التوثيق غير صحيح', '-2': 'رقم الجوال فارغ', '-3': 'نص الرسالة فارغ', '-4': 'اسم المرسل غير صحيح', '-5': 'لا يوجد رصيد كافٍ' };
                Swal.fire({ icon: 'error', title: 'خطأ', text: msgs[code] ?? 'حدث خطأ غير متوقع', timer: 3000, showConfirmButton: false });
            });
        },

        exportToExcel() {
            const table = document.getElementById('reservationTable')?.cloneNode(true);
            if (!table) return;
            const ws = XLSX.utils.table_to_sheet(table);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'الحضور');
            XLSX.writeFile(wb, `حضور-${this.grade?.name ?? ''}.xlsx`);
        },
    },

    async mounted() {
        if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
        if (!this.gradeStore.allGrades.length)   await this.gradeStore.fetchAllGrades();
        this.loadTemplates();
        this.fetchAttendance();
    },
};
</script>

<style scoped>
/* The table is the page's dedicated two-axis scroll region. Keeping the
   region below the sticky navbar prevents its header from hiding underneath
   navigation while the single table keeps header/body columns aligned. */
#reservationTable.attendance-table {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    position: sticky;
    top: var(--navbar-height, 64px);
    z-index: 1;
    overflow-x: auto;
    overflow-y: auto;
    max-height: calc(100vh - var(--navbar-height, 64px) - 1rem);
    max-height: calc(100dvh - var(--navbar-height, 64px) - 1rem);
    -webkit-overflow-scrolling: touch;
}
.attendance-table > table {
    min-width: max-content;
}
.attendance-table th,
.attendance-table td {
    white-space: nowrap;
}

/* Stick to the top of the table scroll region; the opaque fill hides rows
   underneath while the cells continue to scroll horizontally with the body. */
.attendance-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: var(--table-head-bg-start, #1a1a1a) !important;
    background-image: none !important;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
}

@media print {
    #reservationTable.attendance-table {
        overflow: visible;
        max-height: none;
        position: static;
    }
    .attendance-table thead th {
        position: static;
        box-shadow: none;
    }
}

.att-cell {
    cursor: pointer;
    min-width: 48px;
    transition: background-color 0.12s;
}
.att-cell:hover {
    background-color: rgba(212, 160, 23, 0.08) !important;
}
.att-present {
    background-color: #d4edda;
}
.att-present:hover {
    background-color: #c3e6cb !important;
}
.att-icon-present {
    color: #28a745;
    font-size: 0.95rem;
}
.att-icon-absent {
    color: #dc3545;
    opacity: 0.35;
    font-size: 0.85rem;
}

/* ── QR scan result ── */
.row-highlight {
    animation: row-highlight-flash 2s ease-out;
}
@keyframes row-highlight-flash {
    0%, 40%   { background-color: #fff3cd; }
    100%      { background-color: transparent; }
}
</style>
